import { EmptyMessage } from '../../components/EmptyMessage';
import { ItemCard } from '../../components/ItemCard';
import { Section, Title, TitleRow } from '../../components/Section';
import { SkeletonCard } from '../../components/SkeletonCard';
import { ProductGrid } from './ProductListTemplate.styles';
import type { ProductListItem, ProductListTemplateProps } from './ProductListTemplate.types';

function buildMeta(item: ProductListItem): string | undefined {
  const meta: string[] = [];
  if (item.duration) meta.push(item.duration);
  if (item.optionName) meta.push(item.optionName);
  return meta.length > 0 ? meta.join(' · ') : undefined;
}

export function ProductListTemplate({
  items,
  onSelectItem,
  isLoading,
  titleLabel = 'Products',
  fromPriceLabel = 'From',
  soldOutLabel = 'Sold out',
  noResultsLabel = 'No products available.',
  noImageLabel = 'No image',
}: ProductListTemplateProps) {
  if (isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
        </TitleRow>
        <ProductGrid>
          {Array.from({ length: 6 }).map((_, index) => (
            <SkeletonCard key={index} variant="grid" />
          ))}
        </ProductGrid>
      </Section>
    );
  }

  if (!isLoading && items.length === 0) {
    return (
      <Section>
        <TitleRow>
          <Title>{titleLabel}</Title>
        </TitleRow>
        <EmptyMessage>{noResultsLabel}</EmptyMessage>
      </Section>
    );
  }

  return (
    <Section>
      <TitleRow>
        <Title>{titleLabel}</Title>
      </TitleRow>
      <ProductGrid>
        {items.map((item) => (
          <ItemCard
            key={item.id}
            image={{ src: item.imageUrl, alt: item.name, placeholderLabel: noImageLabel }}
            imageOverlay
            title={item.name}
            meta={buildMeta(item)}
            statusBadge={item.unavailable ? soldOutLabel : undefined}
            imageBadge={item.promotionName || undefined}
            price={
              !item.unavailable && item.price
                ? {
                    amount: item.price.amount,
                    currencyCode: item.price.currencyCode,
                    label: fromPriceLabel,
                    discount: item.discount,
                  }
                : undefined
            }
            unavailable={item.unavailable}
            ariaLabel={item.name}
            onSelect={() => onSelectItem(item.id)}
          />
        ))}
      </ProductGrid>
    </Section>
  );
}
